/** Max recipients per WhatsApp send API call. */
export const WA_SEND_CHUNK_SIZE = 50;

/**
 * Split an array into contiguous chunks of `size`.
 * @template T
 * @param {T[]} items
 * @param {number} [size=WA_SEND_CHUNK_SIZE]
 * @returns {T[][]}
 */
export function chunkArray(items, size = WA_SEND_CHUNK_SIZE) {
  const list = Array.isArray(items) ? items : [];
  const chunkSize = Number(size) > 0 ? Number(size) : WA_SEND_CHUNK_SIZE;
  if (list.length === 0) return [];
  const chunks = [];
  for (let i = 0; i < list.length; i += chunkSize) {
    chunks.push(list.slice(i, i + chunkSize));
  }
  return chunks;
}

/**
 * Walk common API envelopes for a `results` array.
 * @param {unknown} response
 * @returns {unknown[]}
 */
export function extractSendResults(response) {
  if (!response || typeof response !== 'object') return [];

  const candidates = [
    response.results,
    response?.data?.results,
    response?.data?.data?.results,
    response?.data?.data?.data?.results,
    response?.summary?.results
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate;
  }

  return [];
}

/**
 * Count success/fail from per-recipient `results[].ok` (falls back to `.success`).
 * If the API returns no results array, treat the whole chunk as success or fail
 * based on `chunkAssumedOk`.
 *
 * @param {unknown} response
 * @param {{ chunkSize: number, chunkAssumedOk?: boolean }} options
 * @returns {{ ok: number, fail: number }}
 */
export function summarizeChunkResults(response, { chunkSize, chunkAssumedOk = true }) {
  const results = extractSendResults(response);
  if (results.length === 0) {
    const n = Number(chunkSize) || 0;
    return chunkAssumedOk ? { ok: n, fail: 0 } : { ok: 0, fail: n };
  }

  let ok = 0;
  let fail = 0;
  for (const entry of results) {
    if (entry == null || typeof entry !== 'object') {
      fail += 1;
      continue;
    }
    if (entry.ok === true || entry.success === true) ok += 1;
    else fail += 1;
  }
  return { ok, fail };
}

/**
 * @param {{ ok: number, fail: number, total: number, labelSingular?: string, labelPlural?: string }} summary
 */
export function formatBulkSendToast({
  ok,
  fail,
  total,
  labelSingular = 'recipient',
  labelPlural = 'recipients'
}) {
  const label = total === 1 ? labelSingular : labelPlural;
  if (fail === 0) {
    return `Sent successfully to ${ok} ${label}.`;
  }
  if (ok === 0) {
    return `Failed to send to ${fail} of ${total} ${label}.`;
  }
  return `Sent to ${ok} of ${total} ${label} (${fail} failed).`;
}
